// ApplicationForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, 
  Grid, 
  Box, 
  Typography, 
  Paper, 
  Card, 
  CardContent, 
  CardMedia,
  Divider,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom'; // Import RouterLink
import Header from '../components/Header/Header';
import HeroSection from '../components/Header/herosection';
import ApplicationFormComponent from '../components/ApplicationForm/appformcomp';
import PublicPageTheme from '../components/PublicPageTheme';
import { useOrg } from '../context/OrgContext';
// Import scraped content
import ageroContent from '../data/agero-content.json';

const ApplicationFormPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { name: companyName, isPlatform, org, supportEmail, supportPhone, applicantSupportEmail, applicantSupportPhone } = useOrg();
  // Who answers questions about the application: White Knight's provider relations, or the partner's applicant contact
  const questionsEmail = isPlatform ? supportEmail : applicantSupportEmail;
  const questionsPhone = isPlatform ? supportPhone : applicantSupportPhone;

  useEffect(() => {
    document.title = `Become a Provider — ${companyName}`;
    return () => { document.title = companyName; };
  }, [companyName]);

  const [formData, setFormData] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // If your form includes file uploads
      const submissionData = new FormData();
      // Append all form fields to submissionData
      Object.keys(formData).forEach((key) => {
        submissionData.append(key, formData[key]);
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications`, {
        method: 'POST',
        body: submissionData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      setShowSuccessMessage(true);
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Failed to submit application');
    }
  };

  // The marketing copy is written for White Knight; a partner gets the same structure with its own
  // name, and its About text from the setup wizard when it has written one.
  const content = useMemo(() => {
    if (isPlatform) return ageroContent;
    const swap = (s) => String(s).replace(/White Knight Motor Club|White Knight/g, companyName);
    const walk = (v) => (typeof v === 'string' ? swap(v) : Array.isArray(v) ? v.map(walk) : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)])) : v);
    const branded = walk(ageroContent);
    if (org?.content?.about) branded.about.description = org.content.about;
    if (org?.content?.heroText) branded.hero.subtitle = org.content.heroText;
    // The partner's own feature cards and call to action (setup wizard → About & reviews)
    const pp = org?.content?.providerPage || {};
    const cards = (pp.features || []).filter((f) => f.title || f.text);
    if (cards.length) branded.features = Object.fromEntries(cards.map((f, i) => [i, { title: f.title, description: f.text, imageUrl: f.imageUrl || '' }]));
    if (pp.ctaTitle) branded.cta.title = pp.ctaTitle;
    if (pp.ctaText) branded.cta.description = pp.ctaText;
    return branded;
  }, [isPlatform, companyName, org]);
  const providerPage = (!isPlatform && org?.content?.providerPage) || {};

  // Convert features object to array for mapping
  const featuresArray = Object.values(content.features);
  // A partner's own customer reviews (from the setup wizard) replace the stock testimonials
  const partnerReviews = !isPlatform ? (org?.content?.reviews || []).filter((r) => r.text) : [];
  const testimonialsArray = partnerReviews.length
    ? partnerReviews.map((r) => ({ quote: r.text, author: `${r.name || 'A customer'}${r.source ? ` · ${r.source}` : ''}` }))
    : Object.values(content.testimonials);
  // What the company requires of providers (partners set this in the wizard)
  const reqs = !isPlatform ? org?.providerRequirements : null;
  const insuranceLabels = { generalLiability: 'General liability', autoLiability: 'Auto liability', cargo: 'Cargo', onHook: 'On-hook', garageKeepers: 'Garage keepers' };
  const requirementLines = reqs ? [
    ...Object.entries(reqs.insurance || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => `${insuranceLabels[k] || k} insurance of at least $${Number(v).toLocaleString()}`),
    ...(reqs.backgroundCheck ? ['Background checks for every driver'] : []),
    ...(reqs.w9 ? ['A current W-9 on file'] : []),
    ...(reqs.businessLicense ? ['A valid business license'] : []),
    ...((reqs.vehicleTypes || []).length ? [`Equipment: ${reqs.vehicleTypes.join(', ')}`] : []),
    ...(reqs.custom || [])
  ] : [];

  return (
    <PublicPageTheme>
    <Box sx={{ backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
      {/* Header with Logo */}
      <Header />

      {/* Hero Section */}
      <HeroSection content={content.hero} imageUrl={providerPage.heroImageUrl} />

      {/* Features Section */}
      <Box id="services-section" sx={{ py: 8, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              color: '#333'
            }}
          >
            Our Services
          </Typography>
          
          <Grid container spacing={4}>
            {featuresArray.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  {/* Feature Image */}
                  <CardMedia
                    component="img"
                    height="140"
                    image={feature.imageUrl || `/images/feature-${index === 0 ? 'roadside' : index === 1 ? 'network' : 'digital'}.jpg`}
                    alt={feature.title}
                    sx={{
                      objectFit: 'cover',
                      objectPosition: index === 0 ? 'center 35%' : index === 1 ? 'center 25%' : 'center 20%' // Adjust each image to center faces
                    }}
                  />
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography 
                      variant="h5" 
                      component="h3" 
                      gutterBottom
                      sx={{ fontWeight: 'medium', color: 'primary.main' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about-section" sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 'bold', color: '#333' }}
              >
                {content.about.title}
              </Typography>
              <Typography 
                variant="body1" 
                paragraph
                sx={{ fontSize: '1.1rem', mb: 4 }}
              >
                {content.about.description}
              </Typography>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink} // Use RouterLink for navigation
                to="/learn-more"       // Link to the new page
                sx={{
                  py: 1,
                  px: 3,
                  textTransform: 'none',
                  borderWidth: 2
                }}
              >
                Learn More
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {/* About Image */}
              <Box
                component="img"
                src={providerPage.aboutImageUrl || '/images/about-image.jpg'}
                alt={`About ${companyName}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '350px',
                  objectFit: 'cover',
                  objectPosition: 'center 30%', // Adjust to center faces
                  borderRadius: 2
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: 8, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              color: '#333'
            }}
          >
            What Our Partners Say
          </Typography>
          
          <Grid container spacing={4}>
            {testimonialsArray.map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4,
                    height: '100%',
                    borderRadius: 2,
                    position: 'relative'
                  }}
                >
                  {/* Quote mark */}
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      position: 'absolute',
                      top: 10,
                      left: 20,
                      color: '#f0f0f0',
                      fontSize: '6rem',
                      zIndex: 0,
                      lineHeight: 1
                    }}
                  >
                    "
                  </Typography>
                  
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        fontSize: '1.1rem', 
                        fontStyle: 'italic',
                        mb: 3
                      }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography 
                      variant="subtitle1" 
                      sx={{ fontWeight: 'bold' }}
                    >
                      {testimonial.author}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 8,
          backgroundColor: 'primary.main',
          backgroundImage: `url("${providerPage.ctaImageUrl || '/images/cta-background.jpg'}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {/* Overlay for text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: (t) => alpha(t.palette.primary.main, 0.85) // the company's primary color
          }}
        />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 3
            }}
          >
            {content.cta.title}
          </Typography>
          
          <Typography 
            variant="h6" 
            paragraph
            sx={{ 
              mb: 4,
              maxWidth: '800px',
              mx: 'auto',
              opacity: 0.9
            }}
          >
            {content.cta.description}
          </Typography>
          
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => {
              const applicationFormSection = document.getElementById('application-form-section');
              if (applicationFormSection) {
                applicationFormSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              textTransform: 'none',
              backgroundColor: 'white',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            {content.cta.buttonText}
          </Button>
        </Container>
      </Box>

      {/* Provider requirements (partner companies set these in their setup wizard) */}
      {(requirementLines.length > 0 || reqs?.intro) && (
        <Box sx={{ py: 6, backgroundColor: '#fff' }}>
          <Container maxWidth="md">
            <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
              What we look for in a provider
            </Typography>
            {reqs?.intro && <Typography variant="body1" align="center" paragraph sx={{ mb: 3 }}>{reqs.intro}</Typography>}
            <Grid container spacing={1.5}>
              {requirementLines.map((line) => (
                <Grid item xs={12} sm={6} key={line}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="body2">{line}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Application Form Section */}
      <Box id="application-form-section" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              mb: 6,
              color: '#333'
            }}
          >
            Application Form
          </Typography>
          
          <Grid container spacing={4}>
            {/* Left Column - Text Content */}
            <Grid item xs={12} md={5}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Join Our Network
                </Typography>
                <Typography variant="body1" paragraph>
                  Thank you for your interest in joining the {companyName} network. As a service provider, you'll gain access to our extensive client base and innovative digital tools.
                </Typography>
                <Typography variant="body1" paragraph>
                  Please complete the application form to begin the process. Our team will review your submission and contact you within 3-5 business days.
                </Typography>
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Requirements
                </Typography>
                <Typography variant="body1" component="div">
                  <ul>
                    {(requirementLines.length ? [...requirementLines, 'Service area information'] : ['Valid business license', 'Proof of insurance', 'Background check for all employees', 'W-9 form', 'Service area information']).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Questions?
                </Typography>
                <Typography variant="body1">
                  If you have any questions about the application process, please contact our provider relations team at <strong>{questionsEmail}</strong>{questionsPhone ? <> or call <strong>{questionsPhone}</strong></> : null}.
                </Typography>
              </Box>
            </Grid>

            {/* Right Column - Application Form */}
            <Grid item xs={12} md={7}>
              <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
                <ApplicationFormComponent
                  formData={formData}
                  setFormData={setFormData}
                  handleSubmit={handleSubmit}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Success and Error Messages */}
          {showSuccessMessage && (
            <Box 
              sx={{ 
                mt: 4, 
                p: 3, 
                backgroundColor: '#e8f5e9', 
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                Application Submitted Successfully!
              </Typography>
              <Typography variant="body1">
                Thank you for applying to join the {companyName} network. Our team will review your application and contact you within 3-5 business days.
              </Typography>
            </Box>
          )}
          
          {errorMessage && (
            <Box 
              sx={{ 
                mt: 4, 
                p: 3, 
                backgroundColor: '#ffebee', 
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ color: '#c62828', fontWeight: 'bold' }}>
                Error
              </Typography>
              <Typography variant="body1">
                {errorMessage}. Please try again or contact our support team for assistance.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      
      {/* Footer removed as requested */}
    </Box>
    </PublicPageTheme>
  );
};

export default ApplicationFormPage;
